import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/auth';

function Home() {
  const { isAuthenticated } = useAuthStore();

  const additionalAudioTools = [
    {
      icon: '🎧',
      title: 'Realtime Speech to Text',
      description: 'Transcribe microphone input in real time while you speak.',
      path: '/realtime-speech-to-text',
      input: 'Live microphone stream',
      output: 'Streaming transcript',
      bestFor: 'Live calls, quick notes',
    },
    {
      icon: '📝',
      title: 'Speech to Text',
      description: 'Upload audio files and convert spoken words into editable text.',
      path: '/speech-to-text',
      input: 'Recorded audio upload',
      output: 'Editable transcript',
      bestFor: 'Podcasts, lectures, meetings',
    },
    {
      icon: '🎛️',
      title: 'Voice Changer',
      description: 'Apply pitch, speed, and gain adjustments for new voice styles.',
      path: '/voice-changer',
      input: 'WAV/MP3 voice clip',
      output: 'Processed WAV',
      bestFor: 'Character voices, creative edits',
    },
    {
      icon: '💥',
      title: 'Text to Sound Effects',
      description: 'Generate production-ready sound effects from short text prompts.',
      path: '/text-to-sound-effects',
      input: 'Text prompt',
      output: 'Generated SFX WAV',
      bestFor: 'Videos, games, social clips',
    },
    {
      icon: '🧼',
      title: 'Voice Isolator',
      description: 'Clean noisy recordings by isolating vocal frequencies.',
      path: '/voice-isolator',
      input: 'WAV/MP3 noisy audio',
      output: 'Cleaner isolated vocal track',
      bestFor: 'Podcast cleanup, call audio',
    },
    {
      icon: '✨',
      title: 'Voice Cleaner',
      description: 'Polish narration by removing hum/static, reducing echo, and normalizing output.',
      path: '/voice-cleaner',
      input: 'Typed or AI-generated text',
      output: 'Cleaner normalized speech',
      bestFor: 'Podcast narration, ad reads, client-ready VO',
    },
  ];

  const audioTools = [
    {
      icon: '🗣️',
      title: 'Text to Speech',
      description:
        'Convert typed or AI-generated text to natural voice with playback, history, and model/voice selection.',
      path: '/text-to-speech',
      input: 'Typed or AI-generated text',
      output: 'Natural speech audio',
      bestFor: 'Narration, voiceovers, instant script-to-audio',
    },
    ...additionalAudioTools,
    {
      icon: '🎭',
      title: 'Voice Cloning',
      description: 'Train and reuse your own AI voice profile from uploaded voice samples.',
      path: '/voice-cloning',
      input: 'Voice samples + script',
      output: 'Reusable custom AI voice',
      bestFor: 'Brand voices, character consistency',
    },
  ];

  const workflowSnapshots = [
    {
      icon: '🎬',
      title: 'Content Creator Workflow',
      steps: 'AI Prompt → Generate & Speak → Voice Cleaner export',
    },
    {
      icon: '📚',
      title: 'Lecture Capture Workflow',
      steps: 'Speech to Text → Realtime Speech to Text → Final transcript edits',
    },
    {
      icon: '🎮',
      title: 'Game/Video Audio Workflow',
      steps: 'Text to Sound Effects → Voice Isolator → Mix into timeline',
    },
  ];

  const platformFeatures = [
    {
      icon: '⚡',
      title: 'Fast Processing Pipeline',
      description: 'Optimized processing for quick output across generation, conversion, and enhancement tools.',
    },
    {
      icon: '🔊',
      title: 'Studio-Ready Audio Output',
      description: 'Download cleaned or generated output in high-quality WAV format.',
    },
    {
      icon: '🧠',
      title: 'AI-Assisted Workflow',
      description: 'From text generation to final audio export, every step is optimized for creators.',
    },
    {
      icon: '🔒',
      title: 'Secure & Private',
      description: 'Your workspace data is protected with authenticated APIs and secure storage patterns.',
    },
  ];

  const pricingPlans = [
    {
      name: 'Free',
      price: '$0',
      reference: 'Start with core features at no cost',
      cta: 'Get Started',
      featured: false,
      features: ['10k monthly credits', 'Standard voices', 'Core tool access'],
    },
    {
      name: 'Starter',
      price: '$2.00',
      reference: 'Competitive entry pricing, almost 60% lower than major alternatives',
      cta: 'Start Starter',
      featured: false,
      features: ['30k monthly credits', 'Commercial usage', 'Instant voice cloning'],
    },
    {
      name: 'Creator',
      price: '$8.80',
      reference: 'Creator-ready features at aggressive value pricing',
      cta: 'Start Creator',
      featured: true,
      features: ['100k monthly credits', 'Pro-quality voices', 'Advanced tool access'],
    },
    {
      name: 'Pro',
      price: '$39.60',
      reference: 'High-volume output with premium value',
      cta: 'Start Pro',
      featured: false,
      features: ['500k monthly credits', 'High quality API output', 'Priority processing queue'],
    },
    {
      name: 'Scale',
      price: '$132.00',
      reference: 'Built for teams that need scale at lower cost',
      cta: 'Contact Sales',
      featured: false,
      features: ['2M monthly credits', 'Workspace collaboration', 'Advanced API throughput'],
    },
  ];

  const aiIntegrationHighlights = [
    'OpenAI-backed text generation models configured inside VoxifyAI',
    'One-click Generate & Speak flow directly in Text to Speech',
    'Use Voice Cleaner on generated content for polished delivery',
  ];

  const totalToolCount = audioTools.length;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-transparent to-cyan-900/20"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                Transform Text into{' '}
                <span className="gradient-text">Natural Speech</span>
              </h1>
              <p className="text-xl text-gray-400 mb-8">
                Build complete audio workflows with VoxifyAI&apos;s {totalToolCount}-tool suite:
                generation, transcription, transformation, cleanup, and cloning in one platform.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                {isAuthenticated ? (
                <Link to="/dashboard" className="btn btn-primary text-lg px-8 py-4">
                  Go to Dashboard
                </Link>
              ) : (
                <>
                  <Link to="/signup" className="btn btn-primary text-lg px-8 py-4 pulse-glow">
                    Get Started Free
                  </Link>
                  <Link to="/login" className="btn btn-outline text-lg px-8 py-4">
                    Sign In
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Audio Tools Section */}
      <section className="py-20 bg-gray-800/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Complete Audio Tool Suite</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Every implemented tool is accessible from one dashboard with dedicated workflows and output controls.
            </p>
          </div>

          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
            {audioTools.map((tool) => (
              <Link
                key={tool.title}
                to={isAuthenticated ? tool.path : '/signup'}
                className="card hover:border-purple-500/50 transition-colors"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500/20 to-cyan-500/20 flex items-center justify-center text-3xl mb-4">
                  {tool.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2">{tool.title}</h3>
                <p className="text-gray-400 mb-3">{tool.description}</p>
                <div className="space-y-1 mb-3">
                  <p className="text-xs text-gray-300"><span className="text-purple-300">Input:</span> {tool.input}</p>
                  <p className="text-xs text-gray-300"><span className="text-purple-300">Output:</span> {tool.output}</p>
                  <p className="text-xs text-gray-300"><span className="text-purple-300">Best for:</span> {tool.bestFor}</p>
                </div>
                <p className="text-sm text-purple-300">Open tool →</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* OpenAI Integration */}
      <section className="py-14 bg-gray-800/20 border-y border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4">OpenAI Content-to-Audio Integration</h2>
            <p className="text-gray-400 max-w-3xl mx-auto">
              Generate scripts and content with AI, then convert to speech right away with Generate &amp; Speak.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {aiIntegrationHighlights.map((item) => (
              <div key={item} className="card text-sm text-gray-300">
                <p>{item}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 text-center">
            <Link
              to={isAuthenticated ? '/text-to-speech' : '/signup'}
              className="btn btn-primary"
            >
              Try Generate &amp; Speak
            </Link>
          </div>
        </div>
      </section>

      {/* Workflow Snapshots */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Detailed Workflow Snapshots</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Mix tools together to move from raw input to publication-ready audio.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {workflowSnapshots.map((item) => (
              <div key={item.title} className="card hover:border-purple-500/50 transition-colors">
                <div className="w-full h-28 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 flex items-center justify-center text-4xl mb-4">
                  {item.icon}
                </div>
                <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-gray-400">{item.steps}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Features */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Built for Creators and Teams</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Production-focused capabilities for reliable daily audio workflows.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {platformFeatures.map((feature) => (
              <div key={feature.title} className="card hover:border-purple-500/50 transition-colors">
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Best Competitive Pricing</h2>
            <p className="text-gray-400 max-w-3xl mx-auto">
              We keep pricing almost 60% lower than bigger market players while delivering more
              tools and creator-first workflows in one platform.
            </p>
          </div>

          <div className="grid md:grid-cols-2 xl:grid-cols-5 gap-6">
            {pricingPlans.map((plan) => (
              <div key={plan.name} className={`card relative ${plan.featured ? 'border-purple-500' : ''}`}>
                {plan.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-purple-500 text-sm rounded-full">
                    Popular
                  </div>
                )}
                <h3 className="text-xl font-semibold mb-2">{plan.name}</h3>
                <p className="text-3xl font-bold mb-1">
                  {plan.price}
                  <span className="text-gray-400 text-base font-normal">/month</span>
                </p>
                <p className="text-xs text-green-400 mb-4">{plan.reference}</p>
                <ul className="space-y-2 mb-6">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-center text-gray-400 text-sm">
                      <span className="text-green-400 mr-2">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to="/signup" className={`btn w-full ${plan.featured ? 'btn-primary' : 'btn-secondary'}`}>
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>

          <p className="text-xs text-gray-500 mt-6 text-center">
            Need Business tier pricing? Custom plans are available with volume-based rates and
            enterprise support options.
          </p>
          <p className="text-xs text-gray-500 mt-1 text-center">
            Pricing is designed to stay highly competitive while preserving premium quality output.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-900/50 to-cyan-900/50">
        <div className="max-w-3xl mx-auto text-center px-4">
          <h2 className="text-3xl font-bold mb-4">Ready to Build with the Full Audio Suite?</h2>
          <p className="text-gray-400 mb-8">
            Start free, then scale with value-first plans priced almost 60% lower than larger providers.
          </p>
          <Link to="/signup" className="btn btn-primary text-lg px-8 py-4">
            Create Free Account
          </Link>
        </div>
      </section>
    </div>
  );
}

export default Home;
